import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, API } from "@/lib/api";
import { Waveform, SealCheck, TrendUp, Article as ArticleIcon, Users, ArrowRight } from "@phosphor-icons/react";
import { HollywoodCube } from "@/components/HollywoodCube";

const coverUrl = (a) => (a?.cover_path ? `${API}/media/${a.cover_path}` : null);

function scoreColor(s) {
  if (s >= 75) return "#E9AC0C";
  if (s >= 50) return "#13C4E8";
  if (s >= 30) return "#9D3FFF";
  return "#DC2450";
}

export default function PublicProfile() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get(`/public/profile/${slug}`).then(({ data }) => setP(data)).catch(() => setNotFound(true));
    api.post(`/public/profile/${slug}/view`).catch(() => {});
    api.get("/public/network").then(({ data }) => setMembers(data.clients || [])).catch(() => {});
  }, [slug]);

  const share = () => {
    navigator.clipboard.writeText(`${window.location.origin}/api/share/profile/${slug}`);
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground">Profile not found.</div>
  );
  if (!p) return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground font-mono text-sm">Loading…</div>
  );

  const rep = p.reputation;

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{`${p.display_name} — Official Profile | Social Media Influencer`}</title>
        <meta name="description" content={p.bio || `${p.display_name}${p.niche ? ` · ${p.niche}` : ""} — verified positive presence powered by Social Media Influencer.`} />
        <link rel="canonical" href={`${window.location.origin}/p/${slug}`} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${p.display_name} — Official Profile`} />
        <meta property="og:description" content={p.bio || ""} />
        {p.avatar && <meta property="og:image" content={p.avatar} />}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(
            slug === "smi-official"
              ? {
                  "@context": "https://schema.org",
                  "@type": "Organization",
                  name: p.display_name,
                  alternateName: p.handle,
                  description: p.bio,
                  url: `${window.location.origin}/p/${slug}`,
                  ...(p.avatar ? { logo: p.avatar } : {}),
                  slogan: p.tagline || undefined,
                }
              : {
                  "@context": "https://schema.org",
                  "@type": "ProfilePage",
                  dateModified: new Date().toISOString(),
                  mainEntity: {
                    "@type": "Person",
                    "@id": `${window.location.origin}/p/${slug}#person`,
                    name: p.display_name,
                    alternateName: p.handle || undefined,
                    description: p.bio || undefined,
                    jobTitle: p.niche || undefined,
                    url: `${window.location.origin}/p/${slug}`,
                    ...(p.avatar ? { image: p.avatar } : {}),
                    ...(rep?.score != null ? { knowsAbout: (p.sections || []).map((s) => s.title) } : {}),
                  },
                }
          )}
        </script>
      </Helmet>

      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2">
            <Waveform size={24} weight="fill" className="text-primary" />
            <span className="font-head font-extrabold text-lg tracking-tighter">SMI</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/network" data-testid="featured-network-badge"
              className="hidden sm:inline-flex items-center gap-1.5 border border-primary/40 text-primary px-3 py-1.5 text-xs font-mono uppercase tracking-widest hover:bg-primary/10 transition-colors duration-200">
              <SealCheck size={13} weight="fill" /> Featured on SMI Network
            </Link>
            <span className="overline text-muted-foreground">VERIFIED PROFILE</span>
          </div>
        </div>
      </header>

      {/* Hero — magazine cover */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1535391879778-3bae11d29a24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxuZW9uJTIwY2luZW1hdGljJTIwY2l0eSUyMG5pZ2h0fGVufDB8fHx8MTc4NjkzMjU0MXww&ixlib=rb-4.1.0&q=85')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/80 to-[#080808]/40" />
        <div className="absolute inset-0 spotlight" />
        <div className="absolute top-6 right-6 hidden lg:block z-10"><HollywoodCube size={104} /></div>
        <div className="max-w-4xl mx-auto px-6 py-16 relative flex flex-col sm:flex-row items-center gap-8">
          <div className="h-36 w-36 shrink-0 border-2 border-primary/50 overflow-hidden bg-secondary/20 shadow-[0_0_40px_rgba(233,172,12,0.25)]">
            {p.avatar ? <img src={p.avatar} alt={p.display_name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center font-head font-bold text-5xl text-primary">{p.display_name.slice(0, 1)}</div>}
          </div>
          <div className="text-center sm:text-left flex-1">
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <h1 className="font-head font-bold text-5xl sm:text-6xl tracking-tighter gold-foil">{p.display_name}</h1>
              <SealCheck size={30} weight="fill" className="text-primary" />
            </div>
            {p.handle && <div className="font-mono text-muted-foreground mt-2">{p.handle}</div>}
            {p.tagline && <div className="text-base text-foreground/90 mt-3 max-w-xl italic">"{p.tagline}"</div>}
            {p.niche && <div className="text-sm text-accent mt-2 overline">{p.niche}</div>}
            {p.bio && <p className="text-muted-foreground mt-4 max-w-xl">{p.bio}</p>}
          </div>
          {rep?.score != null && (
            <div className="text-center glass p-5" data-testid="public-reputation">
              <div className="overline text-muted-foreground">REPUTATION</div>
              <div className="font-head text-6xl font-bold mt-1 tracking-tighter" style={{ color: scoreColor(rep.score), textShadow: `0 0 24px ${scoreColor(rep.score)}55` }}>{rep.score}</div>
              {rep.target && <div className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1"><TrendUp size={12} /> target {rep.target}</div>}
            </div>
          )}
        </div>
      </div>

      {/* Section nav */}
      {p.sections?.length > 0 && (
        <nav className="sticky top-0 z-20 glass border-b border-border" data-testid="profile-section-nav">
          <div className="max-w-4xl mx-auto px-6 flex gap-6 overflow-x-auto h-12 items-center">
            {p.sections.map((s) => (
              <a key={s.id} href={`#${s.id}`} data-testid={`section-link-${s.id}`}
                className="whitespace-nowrap text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors duration-200">
                {s.title}
              </a>
            ))}
          </div>
        </nav>
      )}

      {/* Sections — multi-page brand layout */}
      {p.sections?.length > 0 && (
        <div className="max-w-4xl mx-auto px-6 py-12 space-y-16" data-testid="profile-sections">
          {p.sections.map((s, i) => (
            <section key={s.id} id={s.id} data-testid={`profile-section-${s.id}`} className="scroll-mt-16 fade-up">
              <div className="overline text-primary mb-3">{String(i + 1).padStart(2, "0")} · {s.title}</div>
              <h2 className="font-head font-bold text-3xl sm:text-4xl tracking-tight leading-tight">{s.heading}</h2>
              <p className="text-muted-foreground mt-4 text-lg leading-relaxed max-w-3xl">{s.body}</p>
            </section>
          ))}
        </div>
      )}

      {/* Articles */}
      <div className="max-w-4xl mx-auto px-6 py-12 border-t border-border" id="articles">
        <div className="flex items-center gap-2 mb-6">
          <ArticleIcon size={20} weight="bold" className="text-primary" />
          <h2 className="font-head font-bold text-2xl tracking-tight">Featured on the web</h2>
          <span className="font-mono text-sm text-muted-foreground ml-auto">{p.article_count} articles</span>
        </div>
        {p.articles.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No published articles yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5">
            {p.articles.map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`profile-article-${a.slug}`}
                className="block relative border border-border hover:border-primary transition-colors duration-200 group overflow-hidden">
                <div className="absolute top-3 right-3 z-10 opacity-80 group-hover:opacity-100 transition-opacity duration-200"><HollywoodCube size={30} /></div>
                {coverUrl(a) && (
                  <div className="aspect-[16/9] overflow-hidden border-b border-border">
                    <img src={coverUrl(a)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-head font-bold text-lg group-hover:text-primary transition-colors duration-200 normal-case">{a.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{a.meta_description}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Photo gallery */}
      {p.gallery?.length > 0 && (
        <div className="max-w-4xl mx-auto px-6 py-12 border-t border-border" data-testid="profile-gallery">
          <div className="flex items-center gap-2 mb-6">
            <ArticleIcon size={20} weight="bold" className="text-primary" />
            <h2 className="font-head font-bold text-2xl tracking-tight">Gallery</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {p.gallery.map((g) => (
              <div key={g.id} className="aspect-square border border-border overflow-hidden" data-testid={`public-gallery-${g.id}`}>
                <img src={g.image} alt="gallery" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cross-linked network — every member backlinks to others (domain SEO authority) */}
      {members.filter((m) => m.slug !== slug).length > 0 && (
        <div className="max-w-4xl mx-auto px-6 py-12 border-t border-border" data-testid="related-members">
          <div className="flex items-center gap-2 mb-6">
            <Users size={20} weight="fill" className="text-primary" />
            <h2 className="font-head font-bold text-2xl tracking-tight">More in the SMI Network</h2>
            <Link to="/network" className="text-xs text-primary inline-flex items-center gap-1 ml-auto hover:underline">
              View the whole network <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.filter((m) => m.slug !== slug).slice(0, 6).map((m, i) => (
              <Link key={m.slug} to={`/p/${m.slug}`} data-testid={`related-member-${i}`}
                className="flex items-center gap-3 border border-border hover:border-primary transition-colors duration-200 p-4 group bg-card">
                {m.avatar
                  ? <img src={m.avatar} alt={m.display_name} className="h-12 w-12 rounded-full object-cover border border-primary/40 shrink-0" />
                  : <div className="h-12 w-12 rounded-full bg-secondary border border-border flex items-center justify-center font-head font-bold text-primary shrink-0">{(m.display_name || "?").slice(0, 1)}</div>}
                <div className="min-w-0">
                  <div className="font-head font-bold text-sm truncate group-hover:text-primary transition-colors duration-200 normal-case">{m.display_name}</div>
                  {m.niche && <div className="text-xs text-muted-foreground truncate">{m.niche}</div>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
